// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import React, { MouseEvent, useState, useEffect, useMemo } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import {
  ReviewState,
  Activity,
  ActivityReview,
  Uid,
  Category,
  Organization,
  Volunteer,
} from "../store/schema";
import { coordCache } from "../store/api";
import { nameOf } from "../store/ApiUtil";
import { ACTIVITY_STATUS, URLS, breadcrumbs } from "../urls";
import { update, useWidthGate } from "../UiUtil";
import { durationString, revSortBy, createOuts } from "../util";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { PageContainer } from "../components/PageContainer";
import { Table as ReactTable, Column } from "../components/Table";
import { NavBar, navLink } from "../components/NavBar";

const { debug } = createOuts("ActivityListPage (coordinator)", true);

const WIDTH_GATE = {
  table: 501,
  status: 680,
  category: 860,
  description: 1100,
};

type PageProps = RouteComponentProps<{ status: string }>;

const Page: React.FC<PageProps> = ({ history, match }) => {
  console.time("ActivityListPage");
  debug("======= ActivityListPage (coordinator) =======");
  const widthBucket = useWidthGate(WIDTH_GATE);
  const [state, setState] = useState(coordCache().get());
  const {
    loaded,
    user,
    actIndex,
    actRvwIndex,
    catIndex,
    orgIndex,
    volIndex,
  } = state;
  const { status } = match.params;

  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    return coordCache().subscribe(newState => {
      console.time("ActivityListPage.setState");
      update("setState", state, setState, newState);
      console.timeEnd("ActivityListPage.setState");
    });
  });

  const select = (e: MouseEvent, act: Activity) => {
    e.preventDefault();
    history.push(URLS.cActivitiesDetail(act.uid));
  };

  const activities = [...actIndex.values()].filter(x => {
    const review = actRvwIndex.get(x.uid);
    switch (status) {
      case ACTIVITY_STATUS.all:
        return true;
      case ACTIVITY_STATUS.pending:
        return review == null || review.state === ReviewState.Pending;
      case ACTIVITY_STATUS.approved:
        return review != null && review.state === ReviewState.Approved;
      case ACTIVITY_STATUS.rejected:
        return review != null && review.state === ReviewState.Rejected;
      default:
        return false;
    }
  });

  const crumbs = breadcrumbs().cHome;
  console.log(crumbs);
  console.time("ActivityListPage.render");
  const markup = (
    <PageContainer
      title="Activities List"
      crumbs={crumbs}
      administrators={state.administrators}
      isCoordinator={true}
      school={state.school}
      checks={{ user, loaded }}
    >
      <NavBar
        wide={widthBucket >= WIDTH_GATE.table}
        links={[
          navLink(URLS.cActivities(ACTIVITY_STATUS.pending), "pending"),
          navLink(URLS.cActivities(ACTIVITY_STATUS.rejected), "rejected"),
          navLink(URLS.cActivities(ACTIVITY_STATUS.approved), "approved"),
          navLink(URLS.cActivities(ACTIVITY_STATUS.all), "all"),
        ]}
      />
      <div>Showing {status} activities:</div>
      {widthBucket >= WIDTH_GATE.table ? (
        <ActivityTable
          acts={activities}
          actRvws={actRvwIndex}
          cats={catIndex}
          includeDeleted={includeDeleted}
          orgs={orgIndex}
          vols={volIndex}
          onSelect={select}
          showCategory={widthBucket >= WIDTH_GATE.category}
          showDescription={widthBucket >= WIDTH_GATE.description}
          showStatus={widthBucket >= WIDTH_GATE.status}
        />
      ) : (
          <ActivityList
            acts={activities}
            actRvws={actRvwIndex}
            cats={catIndex}
            includeDeleted={includeDeleted}
            orgs={orgIndex}
            vols={volIndex}
            onSelect={select}
          />
        )}
      <NavBar
        wide={widthBucket >= WIDTH_GATE.table}
        links={[
          navLink(URLS.cActivities(ACTIVITY_STATUS.pending), "pending"),
          navLink(URLS.cActivities(ACTIVITY_STATUS.rejected), "rejected"),
          navLink(URLS.cActivities(ACTIVITY_STATUS.approved), "approved"),
          navLink(URLS.cActivities(ACTIVITY_STATUS.all), "all"),
        ]}
      />
      <div className="nav">
        <button
          type="button"
          onClick={() => setIncludeDeleted(!includeDeleted)}
        >
          {includeDeleted ? "Hide" : "Show"} Deleted Activities
        </button>
      </div>
    </PageContainer>
  );
  console.timeEnd("ActivityListPage.render");
  console.timeEnd("ActivityListPage");
  return markup;
};
export const ActivityListPage = withRouter(Page);

function catName(cats: Map<Uid, Category>, row: Activity) {
  const cat = cats.get(row.categoryUid);
  return cat == null ? "" : cat.name;
}

function orgName(orgs: Map<Uid, Organization>, row: Activity) {
  const org = orgs.get(row.organizationUid);
  return org == null ? "" : org.name;
}

function oneLine(description: string): string {
  const line = description.replace(/\n/g, `${String.fromCodePoint(0x21a9)} `);
  if (line.length <= 120) {
    return line;
  } else {
    return `${line.substring(0, 117)}...`;
  }
}

// ================================================================================

interface ActivityListProps {
  acts: Activity[];
  actRvws: Map<Uid, ActivityReview>;
  cats: Map<Uid, Category>;
  includeDeleted: boolean;
  orgs: Map<Uid, Organization>;
  vols: Map<Uid, Volunteer>;
  onSelect: (e: MouseEvent, record: Activity) => void;
}

const ActivityList: React.FC<ActivityListProps> = props => {
  const { acts, actRvws, cats, includeDeleted, orgs, vols, onSelect } = props;
  const activities = revSortBy(
    includeDeleted ? acts : acts.filter(x => !x.isDeleted),
    x => x.endDate
  );

  function itemClass(activity: Activity): string {
    return activity.isDeleted ? "item deleted" : "item";
  }

  return (
    <ErrorBoundary>
      <div className="narrow-table">
        {activities.map(act => (
          <div
            key={act.uid}
            className={itemClass(act)}
            onClick={e => onSelect(e, act)}
          >
            <div className="title-statement">{act.endDate} {nameOf(act.volunteerUid, vols)}</div>
            <div className="statement">
              {durationString(act.durationInSec)}{" "}
              {act.durationInSec === 3600 ? "hour" : "hours"} (
              {itemStatus(act, actRvws)})
            </div>
            <div className="statement">{orgName(orgs, act)}</div>
            <div className="statement">{catName(cats, act)}</div>
          </div>
        ))}
      </div>
    </ErrorBoundary>
  );
};

// ================================================================================

interface ActivityTableProps {
  acts: Activity[];
  actRvws: Map<Uid, ActivityReview>;
  cats: Map<Uid, Category>;
  includeDeleted: boolean;
  orgs: Map<Uid, Organization>;
  vols: Map<Uid, Volunteer>;
  onSelect: (e: MouseEvent, record: Activity) => void;
  showCategory: boolean;
  showDescription: boolean;
  showStatus: boolean;
}

const ActivityTable: React.FC<ActivityTableProps> = props => {
  const {
    acts,
    actRvws,
    cats,
    includeDeleted,
    orgs,
    vols,
    onSelect,
    showCategory,
    showDescription,
    showStatus,
  } = props;
  console.time("ActivityListPage.Table");
  const columns: Column<Activity>[] = useMemo(() => [
    {
      Header: "Date",
      id: "date",
      accessor: row => row.endDate,
      minWidth: 100,
    },
    {
      Header: "Volunteer",
      id: "volunteer",
      accessor: row => nameOf(row.volunteerUid, vols),
      minWidth: 100,
    },
    {
      Header: "Hours Worked",
      id: "duration",
      accessor: row => durationString(row.durationInSec),
      minWidth: 80,
    },
    {
      Header: "Organization",
      id: "organization",
      accessor: row => orgName(orgs, row),
      minWidth: 160,
    },
    ...(!showCategory ? [] : [{
      Header: "Category",
      id: "category",
      accessor: (row: Activity) => catName(cats, row),
      minWidth: 160,
    }]),
    ...(!showDescription ? [] : [{
      Header: "Description",
      id: "description",
      accessor: (row: Activity) => oneLine(row.description),
      minWidth: 200,
      show: showDescription,
    }]),
    ...(!showStatus ? [] : [{
      Header: "Status",
      id: "status",
      accessor: (row: Activity) => itemStatus(row, actRvws),
      minWidth: 120,
      show: showStatus,
    }]),
  ], [actRvws, cats, orgs, showCategory, showDescription, showStatus, vols]);

  const markup = (
    <ErrorBoundary>
      <ReactTable
        data={includeDeleted ? acts : acts.filter(x => !x.isDeleted)}
        columns={columns}
        isDeleted={x => !!x.isDeleted}
        onSelect={onSelect}
        show-pagination={false}
      />
    </ErrorBoundary>
  );
  console.timeEnd("ActivityListPage.Table");
  return markup;
};

function itemStatus(
  activity: Activity,
  actRvws: Map<Uid, ActivityReview>
): string {
  if (activity.isDeleted) {
    return "deleted";
  }
  const review = actRvws.get(activity.uid);
  const state = review == null ? ReviewState.Pending : review.state;
  switch (state) {
    case ReviewState.Approved:
      return "approved";
    case ReviewState.Pending:
      return "pending";
    case ReviewState.Rejected:
      return "questioned";
    default:
      return "INTERNAL ERROR";
  }
}
